/** Pure (or near-pure) helpers powering the Retouch tool's
 *  per-dab pixel pipelines. Extracted from `useFabricCanvas` so
 *  they can be unit-tested in isolation; the hook imports them
 *  back. Keep this file dependency-free (no React, no Fabric)
 *  so it stays cheap to test. */

import { applyGaussianBlurPixels, buildGaussianKernel1D } from './filters/gaussian-blur';

/** Build a circular brush-mask canvas of `radius * 2` × `radius
 *  * 2`. Mask alpha is 1 inside a hard center radius (radius ×
 *  hardness) and falls linearly to 0 at `radius`. `hardness`
 *  (0–1) controls the falloff shape: 1 = sharp circle (step at
 *  radius), 0 = full Gaussian-like fade from center. */
export function buildCloneBrushMask(radius: number, hardness: number): HTMLCanvasElement {
  const size = radius * 2;
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d');
  if (!ctx) return canvas;
  const hardEdge = hardness * radius;
  const id = ctx.createImageData(size, size);
  const data = id.data;
  for (let y = 0; y < size; y++) {
    for (let x = 0; x < size; x++) {
      const dx = x - radius + 0.5;
      const dy = y - radius + 0.5;
      const dist = Math.sqrt(dx * dx + dy * dy);
      let alpha: number;
      if (dist <= hardEdge) alpha = 1;
      else if (dist >= radius) alpha = 0;
      else alpha = (radius - dist) / Math.max(0.001, radius - hardEdge);
      const i = (y * size + x) * 4;
      data[i] = 255;
      data[i + 1] = 255;
      data[i + 2] = 255;
      data[i + 3] = Math.round(alpha * 255);
    }
  }
  ctx.putImageData(id, 0, 0);
  return canvas;
}

/** Heal Brush tone-matching pass.
 *
 *  Shifts the stamp pixels toward the destination's mean color
 *  within the brush footprint (mask-weighted). After this runs,
 *  the stamp still carries source TEXTURE — every pixel's
 *  deviation from the mean is preserved — but the MEAN itself
 *  matches what's already on the canvas, so the patch blends in
 *  instead of looking pasted-on. This is the cheap approximation
 *  used by classic "Healing Brush" implementations (proper
 *  Poisson blending is the gold standard but ~10× the cost).
 *
 *  The shift is additive in linear sRGB byte space — good for
 *  skin / sky / fabric retouches, the bread-and-butter heal use
 *  cases. Pure-luminance shift would miss color casts; pure-
 *  multiplicative shift would crush highlights. Additive is the
 *  middle ground.
 *
 *  The mask alpha weight makes soft-edged brushes contribute
 *  pixels in the falloff zone less than the hard center. Mask
 *  ImageData is cached on the stroke since the shape doesn't
 *  change within a stroke. */
export function applyHealToneShift(
  stampCtx: CanvasRenderingContext2D,
  destSample: HTMLCanvasElement,
  size: number,
  stroke: { brushMask: HTMLCanvasElement; brushMaskImageData: ImageData | null },
): void {
  // `destSample` is pre-built and pre-cropped by the caller —
  // for bg layers it's the bg image's patch directly, for non-
  // bg layers it's a baseline+overlay composite. Keeping the
  // composite responsibility outside this helper means the lib
  // stays pure and the same helper works for both surfaces.
  const destCtx = destSample.getContext('2d', { willReadFrequently: true });
  if (!destCtx) return;
  const destImg = destCtx.getImageData(0, 0, size, size);
  const stampImg = stampCtx.getImageData(0, 0, size, size);

  if (!stroke.brushMaskImageData) {
    const maskCtx = stroke.brushMask.getContext('2d', { willReadFrequently: true });
    if (!maskCtx) return;
    stroke.brushMaskImageData = maskCtx.getImageData(0, 0, size, size);
  }
  const maskImg = stroke.brushMaskImageData;

  let sumSr = 0,
    sumSg = 0,
    sumSb = 0;
  let sumDr = 0,
    sumDg = 0,
    sumDb = 0;
  let totalW = 0;
  const stampData = stampImg.data;
  const destData = destImg.data;
  const maskData = maskImg.data;
  const len = stampData.length;
  for (let i = 0; i < len; i += 4) {
    const w = maskData[i + 3] / 255;
    if (w <= 0) continue;
    sumSr += stampData[i] * w;
    sumSg += stampData[i + 1] * w;
    sumSb += stampData[i + 2] * w;
    sumDr += destData[i] * w;
    sumDg += destData[i + 1] * w;
    sumDb += destData[i + 2] * w;
    totalW += w;
  }
  if (totalW <= 0) return;
  const dr = sumDr / totalW - sumSr / totalW;
  const dg = sumDg / totalW - sumSg / totalW;
  const db = sumDb / totalW - sumSb / totalW;

  for (let i = 0; i < len; i += 4) {
    if (maskData[i + 3] === 0) continue;
    stampData[i] = Math.max(0, Math.min(255, stampData[i] + dr));
    stampData[i + 1] = Math.max(0, Math.min(255, stampData[i + 1] + dg));
    stampData[i + 2] = Math.max(0, Math.min(255, stampData[i + 2] + db));
  }
  stampCtx.putImageData(stampImg, 0, 0);
}

/** Per-pixel mix shared by the brush-style Retouch variants
 *  (Smudge, Blur, Sharpen). For each pixel in the brush
 *  footprint:
 *      out = dest * (1 - m) + sample * m
 *  where `m = mask_alpha * strength`. The mask weighting makes
 *  soft-edge brushes affect the periphery less; strength is the
 *  user-set 0–1 control.
 *
 *  What `sample` means is variant-specific:
 *    - Smudge:  the trailing pixel buffer carried from the prior
 *               dab (the "wet finger")
 *    - Blur:    the destination patch run through a Gaussian
 *    - Sharpen: the destination patch with an unsharp-mask boost
 *
 *  Returns a new ImageData rather than mutating in place so the
 *  caller can both (a) draw the result onto the surface and
 *  (b) — for Smudge — overwrite the sample buffer with it
 *  (which is what carries the smear forward to the next dab). */
export function applyMaskedPixelMix(
  destImg: ImageData,
  sampleImg: ImageData,
  maskImg: ImageData,
  strength: number,
): ImageData {
  const out = new ImageData(destImg.width, destImg.height);
  const outData = out.data;
  const dData = destImg.data;
  const sData = sampleImg.data;
  const mData = maskImg.data;
  const len = outData.length;
  for (let i = 0; i < len; i += 4) {
    const m = (mData[i + 3] / 255) * strength;
    const inv = 1 - m;
    outData[i] = dData[i] * inv + sData[i] * m;
    outData[i + 1] = dData[i + 1] * inv + sData[i + 1] * m;
    outData[i + 2] = dData[i + 2] * inv + sData[i + 2] * m;
    outData[i + 3] = dData[i + 3] * inv + sData[i + 3] * m;
  }
  return out;
}

/** Brush-spacing ratio for Dodge / Burn / Sponge. 0.25 = a new
 *  dab fires every quarter of the brush radius. Matches
 *  Photoshop's default brush spacing. */
export const DODGE_BURN_SPACING_RATIO = 0.25;

/** Per-dab attenuation for Dodge / Burn / Sponge. Tuned so a
 *  single click delivers a noticeable but small effect — not a
 *  flash to the asymptote. Combined with the per-stroke cap,
 *  one drag = one stamped region of effect, not ~8 piled dabs.
 *  Multiple strokes over the same region build up additively. */
export const DODGE_BURN_PER_DAB_SCALE = 0.25;

/** Maximum layer surface area (in pixels) for which the Sharpen
 *  / Dodge / Burn / Sponge variants will allocate the per-stroke
 *  `appliedMask` Uint8Array. At 1 byte per pixel this is also
 *  the byte budget — 100 MP = 100 MB. Larger layers abandon the
 *  stroke at the first dab. See `useFabricCanvas.ts` for the
 *  rationale (silent graceful-degradation re-introduces the
 *  saturation-on-drag bug; loud failure is preferred). */
export const MAX_RETOUCH_LAYER_PIXELS = 100_000_000;

/** Range-weight curve for Dodge / Burn. `lum` is normalized
 *  luminance (0–1). The three bands are non-overlapping linear
 *  ramps so each pixel is in exactly one band — simpler than
 *  Photoshop's overlapping bell curves and gives a predictable
 *  "shadows-only / midtones-only / highlights-only" feel.
 *  - shadows:    full at L=0,    zero at L≥0.5
 *  - midtones:   peaks at L=0.5, zero at L=0 and L=1
 *  - highlights: zero at L≤0.5,  full at L=1 */
export function dodgeBurnRangeWeight(
  lum: number,
  range: 'shadows' | 'midtones' | 'highlights',
): number {
  if (range === 'shadows') return Math.max(0, 1 - 2 * lum);
  if (range === 'highlights') return Math.max(0, 2 * lum - 1);
  return 1 - Math.abs(2 * lum - 1);
}

/** Build a `padSize × padSize` source canvas containing the
 *  brush footprint at `(dx-pad, dy-pad)` plus a `pad` border,
 *  with the OOB portion of that border filled by edge-extension
 *  of the in-bounds source rather than transparent black.
 *  Without this, the Gaussian-blur step in Blur and Sharpen
 *  pulls transparent neighbors from outside the layer surface,
 *  producing visible dark / bright halos when the brush touches
 *  the canvas edge. Edge-extension is implemented as four
 *  `drawImage(self, …)` calls that replicate the nearest in-
 *  bounds row / column outward into each stripe of the border.
 *
 *  Returns null only if the 2D context can't be obtained. If
 *  the brush footprint is entirely outside the surface, returns
 *  a fully-transparent canvas (caller's mask alpha will suppress
 *  any effect). */
export function buildEdgeExtendedPaddedSource(
  target: HTMLCanvasElement,
  dx: number,
  dy: number,
  size: number,
  pad: number,
): HTMLCanvasElement | null {
  const padSize = size + pad * 2;
  const padded = document.createElement('canvas');
  padded.width = padSize;
  padded.height = padSize;
  const padCtx = padded.getContext('2d');
  if (!padCtx) return null;

  const tw = target.width;
  const th = target.height;

  const srcX = Math.max(0, dx - pad);
  const srcY = Math.max(0, dy - pad);
  const srcXEnd = Math.min(tw, dx - pad + padSize);
  const srcYEnd = Math.min(th, dy - pad + padSize);
  const srcW = Math.max(0, srcXEnd - srcX);
  const srcH = Math.max(0, srcYEnd - srcY);
  const dstX = srcX - (dx - pad);
  const dstY = srcY - (dy - pad);

  if (srcW === 0 || srcH === 0) return padded;

  padCtx.drawImage(target, srcX, srcY, srcW, srcH, dstX, dstY, srcW, srcH);

  if (dstY > 0) {
    padCtx.drawImage(padded, dstX, dstY, srcW, 1, dstX, 0, srcW, dstY);
  }
  if (dstY + srcH < padSize) {
    padCtx.drawImage(
      padded,
      dstX,
      dstY + srcH - 1,
      srcW,
      1,
      dstX,
      dstY + srcH,
      srcW,
      padSize - (dstY + srcH),
    );
  }
  if (dstX > 0) {
    padCtx.drawImage(padded, dstX, 0, 1, padSize, 0, 0, dstX, padSize);
  }
  if (dstX + srcW < padSize) {
    padCtx.drawImage(
      padded,
      dstX + srcW - 1,
      0,
      1,
      padSize,
      dstX + srcW,
      0,
      padSize - (dstX + srcW),
      padSize,
    );
  }

  return padded;
}

/** Whether canvas 2D `ctx.filter` actually applies. iOS Safari
 *  (<16.4) and a number of mobile WebViews / Android browsers
 *  silently ignore it — assigning `ctx.filter = 'blur(1px)'` leaves
 *  the property as '' / 'none' and `drawImage` copies the source
 *  UNblurred. The Retouch Blur/Sharpen brushes relied on it, so on
 *  those browsers Blur did nothing and Sharpen (dest − blurred ≈ 0)
 *  was nearly inert. Detected once and memoized. */
let canvasFilterBlurSupported: boolean | null = null;
export function supportsCanvasFilterBlur(): boolean {
  if (canvasFilterBlurSupported !== null) return canvasFilterBlurSupported;
  try {
    const ctx = document.createElement('canvas').getContext('2d');
    if (!ctx) {
      canvasFilterBlurSupported = false;
      return false;
    }
    ctx.filter = 'blur(1px)';
    canvasFilterBlurSupported = ctx.filter === 'blur(1px)';
  } catch {
    canvasFilterBlurSupported = false;
  }
  return canvasFilterBlurSupported;
}

/** Gaussian-blur the center `size`×`size` region of an edge-extended
 *  padded patch (from `buildEdgeExtendedPaddedSource`) and return it
 *  as ImageData. Uses the GPU-accelerated canvas filter where it's
 *  supported; falls back to the same JS separable Gaussian the
 *  Filter menu uses where it isn't — so the Blur/Sharpen brushes
 *  work on mobile browsers without canvas `ctx.filter` support.
 *
 *  `sigma` is the Gaussian standard deviation in pixels (what CSS
 *  `blur()` takes). The JS path requests `radius ≈ 3σ` because
 *  `applyGaussianBlurPixels` derives σ = radius/3 internally, so the
 *  two paths produce visually matched blurs. */
export function blurredPatchImageData(
  padded: HTMLCanvasElement,
  size: number,
  pad: number,
  sigma: number,
): ImageData | null {
  if (supportsCanvasFilterBlur()) {
    const c = document.createElement('canvas');
    c.width = size;
    c.height = size;
    const ctx = c.getContext('2d', { willReadFrequently: true });
    if (!ctx) return null;
    ctx.filter = `blur(${sigma}px)`;
    ctx.drawImage(padded, -pad, -pad);
    ctx.filter = 'none';
    return ctx.getImageData(0, 0, size, size);
  }
  // JS fallback — blur the full padded patch, then crop the center.
  const pctx = padded.getContext('2d', { willReadFrequently: true });
  if (!pctx) return null;
  const paddedDim = size + pad * 2;
  const blurredPadded = applyGaussianBlurPixels(pctx.getImageData(0, 0, paddedDim, paddedDim), {
    radius: Math.max(1, Math.round(sigma * 3)),
  });
  const src = blurredPadded.data;
  const out = new ImageData(size, size);
  const dst = out.data;
  const rowBytes = size * 4;
  for (let y = 0; y < size; y++) {
    const s = ((y + pad) * paddedDim + pad) * 4;
    dst.set(src.subarray(s, s + rowBytes), y * rowBytes);
  }
  return out;
}

/** Feather (soft-edge) a brush/eraser stamp by Gaussian-blurring its
 *  FULL RGBA — including alpha — so the stroke's edge fades out. The
 *  Filter-menu `applyGaussianBlurPixels` deliberately preserves alpha
 *  (for image-content blur), which is wrong here, so this is a
 *  dedicated pass.
 *
 *  Blurs in PREMULTIPLIED alpha to avoid the dark fringe straight-RGBA
 *  blur leaves at edges (transparent pixels carry RGB 0/black; spreading
 *  their alpha without their premultiplied color tints the feathered
 *  edge black). This matches what canvas `ctx.filter = blur()` does — so
 *  the result is visually equivalent to the GPU path. Returns a new
 *  same-size canvas; `sigma` is the blur radius in px (the CSS feather
 *  amount).
 *
 *  Used as the mobile fallback for brush/eraser feathering where canvas
 *  `ctx.filter` is unsupported (see `supportsCanvasFilterBlur`). The
 *  pass runs once per stroke bake (not per dab), so the JS cost is
 *  negligible. */
export function featherStampCanvas(src: HTMLCanvasElement, sigma: number): HTMLCanvasElement {
  const w = src.width;
  const h = src.height;
  const out = document.createElement('canvas');
  out.width = w;
  out.height = h;
  const sctx = src.getContext('2d', { willReadFrequently: true });
  const octx = out.getContext('2d');
  if (!sctx || !octx || w === 0 || h === 0) return out;

  const radius = Math.max(1, Math.round(sigma * 3));
  const kernel = buildGaussianKernel1D(radius);
  const r = (kernel.length - 1) >> 1;
  const n = w * h;
  const s = sctx.getImageData(0, 0, w, h).data;

  // Premultiply RGB by alpha (0..1); keep alpha in 0..255.
  const pr = new Float32Array(n);
  const pg = new Float32Array(n);
  const pb = new Float32Array(n);
  const pa = new Float32Array(n);
  for (let i = 0, p = 0; p < n; i += 4, p++) {
    const a01 = s[i + 3] / 255;
    pr[p] = s[i] * a01;
    pg[p] = s[i + 1] * a01;
    pb[p] = s[i + 2] * a01;
    pa[p] = s[i + 3];
  }

  // Separable convolution: horizontal into temp buffers, then vertical.
  const tr = new Float32Array(n);
  const tg = new Float32Array(n);
  const tb = new Float32Array(n);
  const ta = new Float32Array(n);
  const maxX = w - 1;
  const maxY = h - 1;
  for (let y = 0; y < h; y++) {
    const row = y * w;
    for (let x = 0; x < w; x++) {
      let ar = 0;
      let ag = 0;
      let ab = 0;
      let aa = 0;
      for (let k = -r; k <= r; k++) {
        const sx = Math.min(maxX, Math.max(0, x + k));
        const wgt = kernel[k + r];
        const idx = row + sx;
        ar += pr[idx] * wgt;
        ag += pg[idx] * wgt;
        ab += pb[idx] * wgt;
        aa += pa[idx] * wgt;
      }
      const o = row + x;
      tr[o] = ar;
      tg[o] = ag;
      tb[o] = ab;
      ta[o] = aa;
    }
  }

  const result = octx.createImageData(w, h);
  const d = result.data;
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      let ar = 0;
      let ag = 0;
      let ab = 0;
      let aa = 0;
      for (let k = -r; k <= r; k++) {
        const sy = Math.min(maxY, Math.max(0, y + k));
        const wgt = kernel[k + r];
        const idx = sy * w + x;
        ar += tr[idx] * wgt;
        ag += tg[idx] * wgt;
        ab += tb[idx] * wgt;
        aa += ta[idx] * wgt;
      }
      const o = (y * w + x) * 4;
      d[o + 3] = aa; // Uint8ClampedArray clamps to 0..255
      if (aa > 0.5) {
        // Un-premultiply: color = premultColor / alpha01 = premultColor * 255 / alpha8.
        const inv = 255 / aa;
        d[o] = ar * inv;
        d[o + 1] = ag * inv;
        d[o + 2] = ab * inv;
      }
      // else leave RGB 0 — fully transparent pixel, color irrelevant.
    }
  }
  octx.putImageData(result, 0, 0);
  return out;
}
